import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { errMsg } from "@/lib/api";
import { MediaPlayer } from "./ExerciseMedia";
import { uploadMedia, detectAspect, previewFromLink, defaultAspect, discardUpload } from "./upload";

const KINDS = [["none", "None"], ["gif", "GIF"], ["youtube", "YouTube"], ["instagram", "Insta reel"], ["video", "Upload video"]];
const ACCEPT = { gif: "image/gif", video: "video/mp4,video/webm,video/quicktime" };

function Pills({ items, value, onChange, testid, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([v, label]) => (
        <button type="button" key={v} disabled={disabled} onClick={() => onChange(v)} data-testid={`${testid}-${v.replace(":", "x")}`}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === v ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#1E293B] text-slate-300 hover:border-slate-500"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// value: { kind, url, path, content_type, aspect, preview } | null
export default function MediaPicker({ value, onChange, onBusy, savedPath }) {
  const [pct, setPct] = useState(null);
  const kind = value?.kind || "none";
  const aspect = value?.aspect || "16:9";
  const dropUnsaved = () => { if (value?.path && value.path !== savedPath) discardUpload(value.path); };

  const setKind = (k) => { dropUnsaved(); onChange(k === "none" ? null : { kind: k, aspect: defaultAspect(k, "") }); };
  const setUrl = (url) => {
    dropUnsaved();
    const a = value?.aspectTouched ? aspect : defaultAspect(kind, url);
    onChange({ ...value, url, path: null, aspect: a, preview: previewFromLink(kind, url, a) });
  };
  const setAspect = (a) => onChange({ ...value, aspect: a, aspectTouched: true, preview: value?.preview && { ...value.preview, aspect: a } });

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Max file size is 50 MB");
    onBusy(true); setPct(0);
    try {
      const detected = await detectAspect(file);
      const a = detected || "16:9";
      const r = await uploadMedia(file, setPct);
      dropUnsaved();
      onChange({ kind, path: r.path, content_type: r.content_type, aspect: a, preview: { kind, url: r.url, aspect: a } });
      toast.success("Media uploaded");
      if (!detected) toast.warning("Couldn't read the video's shape in this browser. Please confirm 16:9 or 9:16 below.");
    } catch (e) { toast.error(errMsg(e)); } finally { onBusy(false); setPct(null); }
  };

  return (
    <div className="space-y-3">
      <Label>Demo media</Label>
      <Pills items={KINDS} value={kind} onChange={setKind} testid="media-kind" disabled={pct !== null} />
      {(kind === "youtube" || kind === "instagram" || kind === "gif") && (
        <Input placeholder={kind === "youtube" ? "https://youtube.com/watch?v=… or /shorts/…" : kind === "instagram" ? "https://www.instagram.com/reel/…" : "Paste a GIF link (https://…)"}
          value={value?.url || ""} onChange={(e) => setUrl(e.target.value.trim())} data-testid="media-url-input" />
      )}
      {(kind === "gif" || kind === "video") && (
        <label className="flex items-center gap-2 text-sm cursor-pointer border border-dashed border-[#1E293B] rounded-md px-4 py-3 hover:border-slate-500" data-testid="media-upload-label">
          {pct !== null ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {kind === "gif" ? "or upload a GIF file" : "Choose MP4, WEBM or MOV (max 50 MB)"}
          <input type="file" accept={ACCEPT[kind]} className="hidden" onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} data-testid="media-file-input" />
        </label>
      )}
      {pct !== null && <Progress value={pct} data-testid="media-upload-progress" />}
      {kind !== "none" && (
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">Aspect ratio</span>
          <Pills items={[["16:9", "16:9 landscape"], ["9:16", "9:16 vertical"]]} value={aspect} onChange={setAspect} testid="media-aspect" />
        </div>
      )}
      {value?.preview && <MediaPlayer media={value.preview} compact />}
    </div>
  );
}
